import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { DEFAULT_KEYMAP } from 'porejs';
import { useReaderKeymap, useReaderKind, useReaderSettings } from './reader.js';
import { TextSettingsPanel } from './text-settings-panel.js';
import { Accordion, SelectField, SliderField, SwitchField, Tabs } from './primitives.js';
const ACTION_LABELS = {
    'toggle-menu': 'Toggle menu',
    'page-right': 'Turn page right',
    'page-left': 'Turn page left',
    'scroll-up': 'Scroll up',
    'scroll-down': 'Scroll down',
    'chapter-forward': 'Next chapter',
    'chapter-back': 'Previous chapter',
    'toggle-fullscreen': 'Fullscreen',
    'cycle-fit': 'Cycle fit mode',
    'toggle-spread-offset': 'Offset double spreads',
    'first-page': 'First page',
    'last-page': 'Last page',
    'toggle-autoscroll': 'Toggle autoscroll',
};
/** Rebind-by-capture editor for the image keymap. */
function KeybindEditor() {
    const [keymap, setKeymap] = useReaderKeymap();
    const [capturing, setCapturing] = useState(null);
    return (_jsxs(_Fragment, { children: [Object.keys(ACTION_LABELS).map((action) => (_jsxs("div", { className: "pore-settings__row", "data-pore-field": true, children: [_jsx("span", { children: ACTION_LABELS[action] }), _jsxs("span", { className: "pore-settings__keys", "data-pore-keys": true, children: [(keymap[action] ?? []).map((k) => (_jsx("kbd", { children: k }, k))), _jsx("button", { type: "button", className: capturing === action ? 'active' : '', onClick: () => setCapturing(action), onKeyDown: (e) => {
                                    if (capturing !== action)
                                        return;
                                    e.preventDefault();
                                    const keys = Array.from(new Set([...(keymap[action] ?? []), e.key])).slice(0, 4);
                                    setKeymap({ [action]: keys });
                                    setCapturing(null);
                                }, children: capturing === action ? 'press a key…' : '+' }), _jsx("button", { type: "button", onClick: () => setKeymap({ [action]: DEFAULT_KEYMAP[action] }), children: "reset" })] })] }, action))), _jsx("div", { className: "pore-settings__row", children: _jsx("button", { type: "button", onClick: () => setKeymap(DEFAULT_KEYMAP), children: "Reset all keybinds" }) })] }));
}
function ImageSettingsPanel({ trailing, extraTabs = [], layout = 'tabs', }) {
    const [s, set] = useReaderSettings();
    const tabs = [
        {
            id: 'layout',
            label: 'Layout',
            content: (_jsxs(_Fragment, { children: [_jsx(SelectField, { label: "Layout", value: s.layout, onValueChange: (layout) => set({ layout }), options: [
                            { value: 'paged-single', label: 'Single page' },
                            { value: 'paged-double', label: 'Double page' },
                            { value: 'continuous-vertical', label: 'Long strip' },
                            { value: 'continuous-horizontal', label: 'Wide strip' },
                        ] }), _jsx(SelectField, { label: "Direction", value: s.direction, onValueChange: (direction) => set({ direction }), options: [
                            { value: 'ltr', label: 'Left to right' },
                            { value: 'rtl', label: 'Right to left' },
                            { value: 'vertical', label: 'Vertical' },
                        ] }), _jsx(SwitchField, { label: "Offset double spreads", checked: !!s.spreadOffset, onCheckedChange: (v) => set({ spreadOffset: v ? 1 : 0 }) }), _jsx(SliderField, { label: "Page gap", min: 0, max: 60, value: s.pageGap, onValueChange: (pageGap) => set({ pageGap }), format: (v) => `${v}px` }), _jsx(SelectField, { label: "Background", value: s.background, onValueChange: (background) => set({ background }), options: [
                            { value: 'theme', label: 'Theme' },
                            { value: 'white', label: 'White' },
                            { value: 'black', label: 'Black' },
                        ] }), _jsx(SelectField, { label: "Progress bar", value: s.progressBar.style, onValueChange: (style) => set({ progressBar: { ...s.progressBar, style } }), options: [
                            { value: 'normal', label: 'Normal' },
                            { value: 'lightbar', label: 'Lightbar' },
                            { value: 'hidden', label: 'Hidden' },
                        ] })] })),
        },
        {
            id: 'fit',
            label: 'Image fit',
            content: (_jsxs(_Fragment, { children: [_jsx(SelectField, { label: "Fit mode", value: s.fit, onValueChange: (fit) => set({ fit }), options: [
                            { value: 'contain', label: 'Contain' },
                            { value: 'width', label: 'Fit width' },
                            { value: 'height', label: 'Fit height' },
                            { value: 'original', label: 'Original size' },
                            { value: 'smart', label: 'Smart' },
                        ] }), _jsx(SwitchField, { label: "Stretch small pages", checked: s.stretchSmallPages, onCheckedChange: (stretchSmallPages) => set({ stretchSmallPages }) }), _jsx(SliderField, { label: "Max width", min: 0, max: 2400, step: 100, value: s.maxWidth ?? 0, onValueChange: (v) => set({ maxWidth: v || null }), format: (v) => (v ? `${v}px` : 'none') }), _jsx(SliderField, { label: "Brightness", min: 0.3, max: 1, step: 0.05, value: s.brightness, onValueChange: (brightness) => set({ brightness }), format: (v) => v.toFixed(2) }), _jsx(SwitchField, { label: "Greyscale", checked: s.greyscale, onCheckedChange: (greyscale) => set({ greyscale }) }), _jsx(SwitchField, { label: "Dim", checked: s.dim, onCheckedChange: (dim) => set({ dim }) })] })),
        },
        {
            id: 'behavior',
            label: 'Behavior',
            content: (_jsxs(_Fragment, { children: [_jsx(SelectField, { label: "Tap edges to turn", value: s.tapToTurn, onValueChange: (tapToTurn) => set({ tapToTurn }), options: [
                            { value: 'directional', label: 'Directional' },
                            { value: 'always-forward', label: 'Always forward' },
                            { value: 'never', label: 'Never' },
                        ] }), _jsx(SelectField, { label: "Wheel in paged mode", value: s.scrollToTurn, onValueChange: (scrollToTurn) => set({ scrollToTurn }), options: [
                            { value: 'off', label: 'Off' },
                            { value: 'wheel', label: 'Turn pages' },
                            { value: 'both', label: 'Turn pages + keys' },
                        ] }), _jsx(SwitchField, { label: "Double-click fullscreen", checked: s.doubleClickFullscreen, onCheckedChange: (doubleClickFullscreen) => set({ doubleClickFullscreen }) }), _jsx(SelectField, { label: "Loading method", value: s.loadingMethod, onValueChange: (loadingMethod) => set({ loadingMethod }), options: [
                            { value: 'native', label: 'Native' },
                            { value: 'blob', label: 'Blob' },
                            { value: 'bitmap', label: 'Bitmap (canvas)' },
                        ] }), _jsx(SelectField, { label: "Preload", value: s.preloadStrategy, onValueChange: (preloadStrategy) => set({ preloadStrategy }), options: [
                            { value: 'window', label: 'Around current page' },
                            { value: 'all', label: 'Whole chapter' },
                        ] }), _jsx(SliderField, { label: "Autoscroll speed", min: 10, max: 200, value: s.autoscrollSpeed, onValueChange: (autoscrollSpeed) => set({ autoscrollSpeed }), format: (v) => `${v} px/s` }), _jsx(SwitchField, { label: "Smooth autoscroll", checked: s.autoscrollSmooth, onCheckedChange: (autoscrollSmooth) => set({ autoscrollSmooth }) }), _jsx(SliderField, { label: "Paged auto-advance", min: 0, max: 20, value: s.pagedAutoAdvanceSeconds, onValueChange: (pagedAutoAdvanceSeconds) => set({ pagedAutoAdvanceSeconds }), format: (v) => (v ? `${v}s` : 'off') }), _jsx(SelectField, { label: "Next chapter after last page", value: String(s.nextChapterAfterLastPage), onValueChange: (v) => set({
                            nextChapterAfterLastPage: v === 'off' || v === 'instant' ? v : Number(v),
                        }), options: [
                            { value: 'off', label: 'Off' },
                            { value: 'instant', label: 'Instant' },
                            { value: '3', label: '3s' },
                            { value: '5', label: '5s' },
                            { value: '10', label: '10s' },
                        ] })] })),
        },
        { id: 'keys', label: 'Keybinds', content: _jsx(KeybindEditor, {}) },
        ...extraTabs,
    ];
    const [tab, setTab] = useState('layout');
    if (layout === 'accordion')
        return _jsx(Accordion, { items: tabs });
    return _jsx(Tabs, { tabs: tabs, value: tab, onValueChange: setTab, ...(trailing ? { trailing } : {}) });
}
/**
 * The settings section set for the current book kind — no dialog chrome.
 * `layout="accordion"` renders stacked `<details>` sections instead of a tab
 * strip (for narrow chrome like a sidebar rail).
 */
export function SettingsPanelBody({ trailing, extraTabs, layout, }) {
    const kind = useReaderKind();
    const props = {
        ...(trailing ? { trailing } : {}),
        ...(extraTabs ? { extraTabs } : {}),
        ...(layout ? { layout } : {}),
    };
    return kind === 'text' ? _jsx(TextSettingsPanel, { ...props }) : _jsx(ImageSettingsPanel, { ...props });
}
/**
 * Reader settings. With `open`/`onOpenChange` (or a `trigger`) it is a Radix
 * modal `Dialog` with a focus trap; otherwise it renders the tab set inline.
 * Both paths use Radix `Tabs` and the headless field primitives.
 */
export function SettingsPanel({ open, onOpenChange, onClose, trigger, extraTabs, className, }) {
    const controlled = open !== undefined || onOpenChange !== undefined || trigger !== undefined;
    // Restore focus to whatever opened the dialog on close — Radix does this
    // for its own `Dialog.Trigger`, but the common case here is a controlled
    // `open` prop with an external toggle button and no trigger.
    const restoreFocusRef = useRef(null);
    useEffect(() => {
        if (open)
            restoreFocusRef.current = document.activeElement;
    }, [open]);
    const close = (_jsx(Dialog.Close, { className: "pore-settings__close", "aria-label": "Close", "data-pore-dialog-close": true, onClick: onClose, children: "\u00D7" }));
    if (!controlled) {
        return (_jsx("div", { className: className ?? 'pore-settings', role: "group", "aria-label": "Reader settings", children: _jsx(SettingsPanelBody, { ...(extraTabs ? { extraTabs } : {}), trailing: onClose ? (_jsx("button", { type: "button", className: "pore-settings__close", "aria-label": "Close", onClick: onClose, children: "\u00D7" })) : undefined }) }));
    }
    return (_jsxs(Dialog.Root, { ...(open !== undefined ? { open } : {}), onOpenChange: (o) => {
            onOpenChange?.(o);
            if (!o)
                onClose?.();
        }, children: [trigger ? _jsx(Dialog.Trigger, { asChild: true, children: trigger }) : null, _jsxs(Dialog.Portal, { children: [_jsx(Dialog.Overlay, { className: "pore-settings__overlay", "data-pore-dialog-overlay": true }), _jsxs(Dialog.Content, { className: className ?? 'pore-settings', "data-pore-dialog": true, "aria-label": "Reader settings", onCloseAutoFocus: (e) => {
                            if (!trigger && restoreFocusRef.current?.isConnected) {
                                e.preventDefault();
                                restoreFocusRef.current.focus();
                            }
                        }, children: [_jsx(Dialog.Title, { className: "pore-sr-only", children: "Reader settings" }), _jsx(SettingsPanelBody, { trailing: close, ...(extraTabs ? { extraTabs } : {}) })] })] })] }));
}
//# sourceMappingURL=settings-panel.js.map